import styleText from './as-option.css?raw';
import { upgradeProperty } from './utils';
import '../text-and-lang/as-text.ts';
import '../overlays/as-hovercard-wrapper.ts';
import '../overlays/as-tooltip-wrapper.ts';
import { createOptionHoverWrapper } from './option-hover-wrapper.ts';

const template = document.createElement('template');
template.innerHTML = `
  <div class="wrapper">
    <span class="indicator" aria-hidden="true"></span>
    <span class="content">
      <slot></slot>
    </span>
  </div>
`;

// One-time (per page) warning for the deprecated `label` attribute. Keyed on
// nothing in particular - one warning is enough to send someone to the docs.
let _warnedLabelDeprecated = false;

/**
 * as-option custom element for use with as-dropdown (light DOM).
 *
 * Its label can be written three ways:
 *
 *   <as-option value="1" default-text="Box"></as-option>            preferred
 *   <as-option value="1" default-text="Box" dict-key="shape.box">   translated
 *   <as-option value="1">Box</as-option>                            slotted
 *
 * `default-text`/`dict-key` are the same pair every other text-bearing
 * component takes, so an option written that way is translated by adding one
 * attribute rather than by restructuring the markup.
 *
 * When either is set, and the option has no content of its own, this element
 * renders an <as-text> into its light DOM and that owns the label.
 *
 * Light DOM rather than the shadow root is deliberate: as-dropdown reads an
 * option's label as `getAttribute('label') ?? textContent`, and observes the
 * option's light-DOM subtree for a translation arriving late. Putting it there
 * means both keep working with no change to as-dropdown at all.
 *
 * Author content always wins over the attributes, and warns. Injecting
 * alongside it would change what textContent reads as, and that is both the
 * displayed label and the last fallback for `value`.
 *
 * `label` is a deprecated alias for `default-text`.
 */
export class AsOption extends HTMLElement {
  static readonly tag = 'as-option' as const;

  static get observedAttributes() {
    return ['value', 'selected', 'disabled', 'label', 'default-text', 'dict-key', 'tooltip-default-text', 'tooltip-dict-key'];
  }

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private _shadowRoot?: ShadowRoot;
  // The <as-text> this element manages, when default-text/dict-key are in use.
  private _managedText: HTMLElement | null = null;

  constructor() {
    super();
    upgradeProperty(this, 'selected');
    upgradeProperty(this, 'disabled');
    upgradeProperty(this, 'value');
    upgradeProperty(this, 'label');
    upgradeProperty(this, 'defaultText');
    upgradeProperty(this, 'dictKey');
  }

  connectedCallback() {
    this.tabIndex = -1;
    this.setAttribute('role', 'option');
    if (!this._shadowRoot) {
      this._shadowRoot = this.attachShadow({ mode: 'open' });
      this._shadowRoot.appendChild(template.content.cloneNode(true));
      this._shadowRoot.adoptedStyleSheets = [
        ...this._shadowRoot.adoptedStyleSheets,
        AsOption._sheet,
      ];
      this._shadowRoot.querySelector('slot')?.addEventListener('slotchange', () => {
        this._handleHovercardsAndTooltips();
      });
    }
    this._warnIfLabelUsed();
    this._syncManagedText();
    this._render();
    this._handleHovercardsAndTooltips();
  }

  /** One-time nudge that `label` has been superseded. It still works. */
  private _warnIfLabelUsed(): void {
    if (_warnedLabelDeprecated || !this.hasAttribute('label')) return;
    _warnedLabelDeprecated = true;
    console.warn(
      `[as-option] the 'label' attribute is deprecated; use 'default-text' instead ` +
      `(same meaning, and it pairs with 'dict-key' for translation).`
    );
  }

  /** The label text this option was given, if any. `label` is the deprecated
   *  spelling of `default-text`, so it answers here too — `default-text` wins
   *  when both are set, which is what lets an app add the new attribute before
   *  deleting the old one. */
  private _fallbackText(): string | null {
    return this.getAttribute('default-text') ?? this.getAttribute('label');
  }

  /** Creates, updates or removes the <as-text> this element owns.
   *
   *  Only runs off attributes, never off slot changes, so writing into our own
   *  light DOM here cannot feed back into the slotchange listener above. */
  private _syncManagedText(): void {
    const fallback = this._fallbackText();
    const dictKey = this.getAttribute('dict-key');

    if (fallback === null && dictKey === null) {
      this._managedText?.remove();
      this._managedText = null;
      return;
    }

    // Content the author wrote wins, and we stay out of the way entirely.
    // Injecting alongside it would change what textContent reads as, which is
    // both the displayed label and the last fallback for `value`.
    if (this._hasAuthorContent()) {
      this._managedText?.remove();
      this._managedText = null;
      console.warn(
        `[as-option] has content of its own as well as a 'default-text'/'dict-key'/'label' ` +
        `attribute. The content is used and the attribute is ignored — remove one.`,
        this
      );
      return;
    }

    if (!this._managedText) {
      this._managedText = document.createElement('as-text');
      this.prepend(this._managedText);
    }

    if (fallback === null) this._managedText.removeAttribute('default-text');
    else this._managedText.setAttribute('default-text', fallback);

    if (dictKey === null) this._managedText.removeAttribute('dict-key');
    else this._managedText.setAttribute('dict-key', dictKey);
  }

  /** Light-DOM content the author wrote, ignoring our own <as-text> and the
   *  <as-hovercard> children that get relocated into the shadow root. */
  private _hasAuthorContent(): boolean {
    return Array.from(this.childNodes).some((node) => {
      if (node === this._managedText) return false;
      if (node.nodeType === Node.ELEMENT_NODE) {
        return (node as Element).tagName.toLowerCase() !== 'as-hovercard';
      }
      return (node.textContent ?? '').trim() !== '';
    });
  }

  private _handleHovercardsAndTooltips(): void {
    const tooltipDefaultText = this.getAttribute('tooltip-default-text');
    const tooltipDictKey = this.getAttribute('tooltip-dict-key');
    const hovercards = Array.from(this.querySelectorAll('as-hovercard'));

    if (hovercards.length === 0 && !tooltipDefaultText && !tooltipDictKey) return;

    if (!this._shadowRoot) return;
    const contentSpan = this._shadowRoot.querySelector('.content');
    if (!contentSpan) return;

    // Check if already wrapped
    if (contentSpan.querySelector('as-hovercard-wrapper') || contentSpan.querySelector('as-tooltip-wrapper')) return;

    const slot = contentSpan.querySelector('slot');
    if (!slot) return;

    if (hovercards.length > 0) {
      // TODO: Keep a list of all hovercard siblings. 
      // In the future we can maybe build a multi-panel view.
      // For now, only use the first one and hide the rest.
      const mainHovercard = hovercards[0];
      hovercards.slice(1).forEach(hc => (hc as HTMLElement).style.display = 'none');

      // Remove from light DOM to move it into shadow DOM wrapper
      mainHovercard.remove();

      const wrapper = createOptionHoverWrapper(null, null, true);
      slot.parentNode?.insertBefore(wrapper, slot);
      wrapper.appendChild(slot);
      wrapper.appendChild(mainHovercard);
    } else if (tooltipDefaultText || tooltipDictKey) {
      const wrapper = createOptionHoverWrapper(tooltipDefaultText, tooltipDictKey, false);
      slot.parentNode?.insertBefore(wrapper, slot);
      wrapper.appendChild(slot);
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue !== newValue) {
      if (name === 'disabled') {
        this._updateCursor();
      }
      if (name === 'label') {
        this._warnIfLabelUsed();
      }
      // Guarded on _shadowRoot so a value set before connect is left for
      // connectedCallback - prepending to light DOM is fine either way, but
      // doing it once keeps the ordering predictable.
      if ((name === 'default-text' || name === 'dict-key' || name === 'label') && this._shadowRoot) {
        this._syncManagedText();
      }
      if (name === 'tooltip-default-text' || name === 'tooltip-dict-key') {
        const wrapper = this._shadowRoot?.querySelector('as-tooltip-wrapper');
        if (wrapper) {
          const targetAttr = name === 'tooltip-default-text' ? 'default-text' : 'dict-key';
          if (newValue === null) {
            wrapper.removeAttribute(targetAttr);
          } else {
            wrapper.setAttribute(targetAttr, newValue);
          }
        } else {
          this._handleHovercardsAndTooltips();
        }
      }
      this._render();
    }
  }

  get value() {
    return this.getAttribute('value') ?? this.textContent ?? '';
  }
  set value(val: string) {
    this.setAttribute('value', val);
  }

  get selected() {
    return this.hasAttribute('selected');
  }
  set selected(val: boolean) {
    if (val) {
      this.setAttribute('selected', '');
    } else {
      this.removeAttribute('selected');
    }
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get defaultText() {
    return this.getAttribute('default-text') ?? '';
  }
  set defaultText(val: string) {
    this.setAttribute('default-text', val);
  }

  get dictKey() {
    return this.getAttribute('dict-key') ?? '';
  }
  set dictKey(val: string) {
    this.setAttribute('dict-key', val);
  }

  /** The option's resolved display text — what a translation, `default-text`,
   *  the deprecated `label` attribute or slotted content ended up as. Reading
   *  it is not deprecated; only setting it is. */
  get label() {
    return this.getAttribute('label') ?? this.textContent ?? '';
  }
  /** @deprecated Set `defaultText` (or the `default-text` attribute) instead. */
  set label(val: string) {
    this.setAttribute('label', val);
  }

  private _render() {
    this.classList.toggle('selected', this.selected);
    this.classList.toggle('disabled', this.disabled);
    this.setAttribute('aria-selected', this.selected ? 'true' : 'false');
    this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
    this._updateCursor();
  }

  private _updateCursor() {
    this.style.cursor = this.disabled ? 'not-allowed' : 'pointer';
  }
}

customElements.define(AsOption.tag, AsOption);
